import React from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import AccountBalanceRoundedIcon from "@mui/icons-material/AccountBalanceRounded";
import AccountTreeRoundedIcon from "@mui/icons-material/AccountTreeRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import PercentRoundedIcon from "@mui/icons-material/PercentRounded";
import PointOfSaleRoundedIcon from "@mui/icons-material/PointOfSaleRounded";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SpaceDashboardRoundedIcon from "@mui/icons-material/SpaceDashboardRounded";
import SwapHorizRoundedIcon from "@mui/icons-material/SwapHorizRounded";
import { Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import ModuleDisabledState from "../../../components/dashboard/ModuleDisabledState";
import { projectsApi } from "../../../api/projects";
import { erpApi } from "../../../api/erp";
import { useProjects } from "../../../context/ProjectsContext";
import { isProjectModuleEnabled } from "../../../utils/projectModules";
import { ERP_BRAND } from "../../../utils/erp";
import {
  hasAnyProjectPermission,
  hasProjectPermission,
  normalizeProjectAccess,
  projectRoleLabel,
} from "../../../utils/projectAccess";

const PRIMARY_TABS = [
  {
    key: "overview",
    label: "Estado del negocio",
    shortLabel: "Resumen",
    helper: "Indicadores, alertas y próximos pasos",
    path: "",
    icon: <SpaceDashboardRoundedIcon />,
    permissions: ["erp.overview.read"],
  },
  {
    key: "items",
    label: "Ítems",
    shortLabel: "Ítems",
    helper: "Productos, servicios e insumos",
    path: "items",
    icon: <CategoryRoundedIcon />,
    permissions: ["items.read"],
  },
  {
    key: "movements",
    label: "Qué pasó",
    shortLabel: "Movimientos",
    helper: "Compras, ventas, ajustes y gastos",
    path: "movimientos",
    icon: <ReceiptLongRoundedIcon />,
    permissions: ["erp.movements.read"],
  },
  {
    key: "cash",
    label: "Cajas y cuentas",
    shortLabel: "Dinero",
    helper: "Efectivo, bancos y medios de pago",
    path: "caja",
    icon: <PointOfSaleRoundedIcon />,
    permissions: ["cash.registers.read", "cash.sessions.read", "cash.movements.read"],
  },
  {
    key: "stock",
    label: "Inventario",
    shortLabel: "Stock",
    helper: "Existencias y disponibilidad real",
    path: "stock",
    icon: <InventoryRoundedIcon />,
    permissions: ["erp.stock.read"],
  },
  {
    key: "accounting",
    label: "Contabilidad avanzada",
    shortLabel: "Contabilidad",
    helper: "Libro, cuentas e impuestos cuando los necesites",
    path: "ledger",
    icon: <AccountBalanceRoundedIcon />,
    permissions: ["erp.ledger.read", "erp.transactions.read", "erp.tax_profiles.read"],
  },
];

const ACCOUNTING_TABS = [
  {
    key: "ledger",
    label: "Libro contable",
    path: "ledger",
    icon: <AccountBalanceRoundedIcon />,
    permissions: ["erp.ledger.read"],
  },
  {
    key: "transactions",
    label: "Cobros y pagos",
    path: "transacciones",
    icon: <SwapHorizRoundedIcon />,
    permissions: ["erp.transactions.read"],
  },
  {
    key: "accounts",
    label: "Plan de cuentas",
    path: "cuentas",
    icon: <AccountTreeRoundedIcon />,
    permissions: ["erp.ledger.read"],
  },
  {
    key: "taxes",
    label: "Impuestos",
    path: "impuestos",
    icon: <PercentRoundedIcon />,
    permissions: ["erp.tax_profiles.read"],
  },
];

const WORKFLOW_STEPS = [
  { number: "1", title: "Ubicá tu dinero", detail: "Cajas, bancos y billeteras reales." },
  { number: "2", title: "Registrá lo cotidiano", detail: "Ventas, compras, gastos y stock." },
  { number: "3", title: "Mirá qué cambió", detail: "Saldos, inventario y resultado se actualizan." },
];

const BENEFIT_ITEMS = [
  ["Ventas", "Ingresos y cobros"],
  ["Compras", "Costo y existencias"],
  ["Gastos", "Resultado real"],
  ["Caja", "Dónde está el dinero"],
  ["Stock", "Qué queda disponible"],
];

function canAccessTab(access, tab) {
  return hasAnyProjectPermission(access, tab.permissions || []);
}

function pathSegment(pathname) {
  return String(pathname || "").split("/").filter(Boolean).pop() || "";
}

function primaryValue(pathname) {
  const segment = pathSegment(pathname);
  if (["ledger", "transacciones", "cuentas", "impuestos"].includes(segment)) return "accounting";
  if (segment === "items") return "items";
  if (segment === "movimientos") return "movements";
  if (segment === "caja") return "cash";
  if (segment === "stock") return "stock";
  return "overview";
}

function accountingValue(pathname) {
  const segment = pathSegment(pathname);
  return ACCOUNTING_TABS.some((tab) => tab.path === segment) ? segment : "ledger";
}

function PrimaryTabLabel({ tab, badge = "" }) {
  return (
    <Box sx={{ minWidth: 0, textAlign: "left" }}>
      <Typography
        component="span"
        sx={{
          display: "block",
          color: "inherit",
          fontSize: { xs: ".77rem", sm: ".82rem" },
          fontWeight: 950,
          lineHeight: 1.15,
        }}
      >
        <Box component="span" sx={{ display: { xs: "inline", sm: "none" } }}>
          {tab.shortLabel}
        </Box>
        <Box component="span" sx={{ display: { xs: "none", sm: "inline" } }}>
          {tab.label}
        </Box>
      </Typography>
      {badge ? (
        <Typography
          component="span"
          sx={{
            display: "block",
            color: "rgba(243,90,58,.88)",
            fontSize: ".62rem",
            fontWeight: 950,
            letterSpacing: 0.35,
            lineHeight: 1.2,
            mt: 0.35,
          }}
        >
          {badge}
        </Typography>
      ) : null}
      <Typography
        component="span"
        sx={{
          display: { xs: "none", lg: "block" },
          color: "rgba(25,36,47,.56)",
          fontSize: ".68rem",
          fontWeight: 750,
          lineHeight: 1.25,
          mt: 0.45,
          whiteSpace: "normal",
        }}
      >
        {tab.helper}
      </Typography>
    </Box>
  );
}

export default function ProjectErpLayout() {
  const { projectId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { projects, activeProject, setActiveProject, loadProjects } = useProjects();
  const [project, setProject] = React.useState(null);
  const [access, setAccess] = React.useState(normalizeProjectAccess());
  const [bootstrap, setBootstrap] = React.useState({
    accounts: [],
    registers: [],
    taxProfiles: [],
    openSessions: [],
    progress: { itemCount: null, movementCount: null, confirmedMovementCount: null },
  });
  const [loading, setLoading] = React.useState(true);
  const [bootstrapping, setBootstrapping] = React.useState(false);
  const [error, setError] = React.useState("");

  const contextProject = React.useMemo(
    () =>
      projects.find(
        (item) => item.slug === projectId || item.id === projectId || item._id === projectId,
      ) || activeProject,
    [projects, projectId, activeProject],
  );
  const resolvedProject = project || contextProject;
  const superAdmin = access.role === "superadmin";
  const enabled = superAdmin || isProjectModuleEnabled(resolvedProject, "system");
  const visibleTabs = React.useMemo(
    () => PRIMARY_TABS.filter((tab) => canAccessTab(access, tab)),
    [access],
  );
  const visibleAccountingTabs = React.useMemo(
    () => ACCOUNTING_TABS.filter((tab) => canAccessTab(access, tab)),
    [access],
  );
  const canBootstrap = hasAnyProjectPermission(access, [
    "erp.movements.read",
    "erp.ledger.read",
    "cash.registers.read",
    "erp.stock.read",
  ]);
  const canReadMoneyPlaces = hasProjectPermission(access, "cash.registers.read");
  const canManageMoneyPlaces = hasProjectPermission(access, "cash.registers.manage");
  const canCreateItems = hasProjectPermission(access, "items.create");
  const canCreateSale = hasProjectPermission(access, "erp.sales.create");
  const canCreatePurchase = hasProjectPermission(access, "erp.purchases.create");
  const canCreateExpense = hasProjectPermission(access, "erp.expenses.create");
  const canReadStock = hasProjectPermission(access, "erp.stock.read");
  const progress = bootstrap.progress || {};
  const needsMoneySetup = canReadMoneyPlaces && !bootstrapping && bootstrap.registers.length === 0;
  const needsFirstItem = progress.itemCount === 0 && canCreateItems;
  const needsFirstMovement = progress.movementCount === 0 && !needsMoneySetup;
  const accountingStarted = Number(progress.confirmedMovementCount || 0) > 0;

  React.useEffect(() => {
    if (!projects.length) loadProjects();
  }, [projects.length, loadProjects]);

  React.useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError("");
    projectsApi
      .get(projectId)
      .then((data) => {
        if (!mounted) return;
        setProject(data.project || null);
        setAccess(normalizeProjectAccess(data.memberAccess || data.member || {}));
        if (data.project) setActiveProject(data.project);
      })
      .catch((err) => {
        if (mounted) setError(err.message || "No se pudo cargar el proyecto");
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [projectId, setActiveProject]);

  const refreshBootstrap = React.useCallback(async () => {
    if (!projectId || !enabled || !canBootstrap) return null;
    try {
      setBootstrapping(true);
      const data = await erpApi.bootstrap(projectId);
      if (data.access) setAccess(normalizeProjectAccess(data.access));
      const next = {
        accounts: data.accounts || [],
        registers: data.registers || [],
        taxProfiles: data.taxProfiles || [],
        openSessions: data.openSessions || [],
        progress: {
          itemCount: data.progress?.itemCount === null || data.progress?.itemCount === undefined
            ? null
            : Number(data.progress.itemCount),
          movementCount: data.progress?.movementCount === null || data.progress?.movementCount === undefined
            ? null
            : Number(data.progress.movementCount),
          confirmedMovementCount: data.progress?.confirmedMovementCount === null || data.progress?.confirmedMovementCount === undefined
            ? null
            : Number(data.progress.confirmedMovementCount),
        },
      };
      setBootstrap(next);
      return next;
    } catch (err) {
      setError(err.message || "No se pudo preparar la estructura del sistema");
      return null;
    } finally {
      setBootstrapping(false);
    }
  }, [projectId, enabled, canBootstrap]);

  React.useEffect(() => {
    if (!loading && enabled && canBootstrap) refreshBootstrap();
  }, [loading, enabled, canBootstrap, refreshBootstrap]);

  const base = `/dashboard/proyectos/${projectId}/sistema`;
  const currentTab = primaryValue(location.pathname);
  const currentAccountingTab = accountingValue(location.pathname);
  const onboardingStage = needsMoneySetup
    ? "money"
    : needsFirstItem
      ? "items"
      : needsFirstMovement
        ? "operations"
        : "ongoing";
  const starterCopy = {
    money: {
      title: "Primero ubicá dónde manejás dinero",
      detail: "Con una caja, banco o billetera real, las ventas y gastos pueden actualizar saldos sin que tengas que llevar cuentas aparte.",
    },
    items: {
      title: "Ahora cargá lo que vendés, comprás o usás",
      detail: "Los ítems conectan cada operación con precios, costos y stock. Después registrar una venta o compra lleva menos pasos.",
    },
    operations: {
      title: "Ya podés registrar la primera operación",
      detail: "Elegí lo que pasó en el negocio. Al confirmar, WebProfit actualiza automáticamente los datos relacionados.",
    },
    ongoing: {
      title: "Registrá lo cotidiano; el control se arma solo",
      detail: "Usá estos accesos para seguir operando. La contabilidad avanzada queda disponible cuando necesites revisar el detalle técnico.",
    },
  }[onboardingStage];
  const starterActions = [
    ...(onboardingStage === "money" && canManageMoneyPlaces
      ? [{ key: "cash-setup", label: "Crear caja o cuenta", icon: <PointOfSaleRoundedIcon />, href: `${base}/caja?crear=1`, primary: true }]
      : []),
    ...(onboardingStage === "items" && canCreateItems
      ? [{ key: "item-create", label: "Crear primer ítem", icon: <CategoryRoundedIcon />, href: `${base}/items?crear=1`, primary: true }]
      : []),
    ...(!["money", "items"].includes(onboardingStage) && canCreateSale
      ? [{ key: "sale", label: "Registrar venta", icon: <ReceiptLongRoundedIcon />, href: `${base}/movimientos?nuevo=1&tipo=sale`, primary: onboardingStage === "operations" }]
      : []),
    ...(!["money", "items"].includes(onboardingStage) && canCreatePurchase
      ? [{ key: "purchase", label: "Registrar compra", icon: <InventoryRoundedIcon />, href: `${base}/movimientos?nuevo=1&tipo=purchase` }]
      : []),
    ...(!["money", "items"].includes(onboardingStage) && canCreateExpense
      ? [{ key: "expense", label: "Registrar gasto", icon: <ReceiptLongRoundedIcon />, href: `${base}/movimientos?nuevo=1&tipo=expense` }]
      : []),
    ...(onboardingStage === "ongoing" && canReadStock
      ? [{ key: "stock", label: "Revisar stock", icon: <InventoryRoundedIcon />, href: `${base}/stock` }]
      : []),
    ...(onboardingStage === "money" && canCreateItems
      ? [{ key: "item-secondary", label: "Cargar un ítem", icon: <CategoryRoundedIcon />, href: `${base}/items?crear=1` }]
      : []),
  ].slice(0, 4);

  React.useEffect(() => {
    if (loading || !enabled || !visibleTabs.length) return;
    if (!visibleTabs.some((tab) => tab.key === currentTab)) {
      const first = visibleTabs[0];
      navigate(first.path ? `${base}/${first.path}` : base, { replace: true });
    }
  }, [loading, enabled, visibleTabs, currentTab, navigate, base]);

  React.useEffect(() => {
    if (loading || currentTab !== "accounting" || !visibleAccountingTabs.length) return;
    if (!visibleAccountingTabs.some((tab) => tab.path === currentAccountingTab)) {
      navigate(`${base}/${visibleAccountingTabs[0].path}`, { replace: true });
    }
  }, [base, currentAccountingTab, currentTab, loading, navigate, visibleAccountingTabs]);

  if (loading) return <Alert severity="info">Cargando gestión del negocio...</Alert>;

  if (!enabled) {
    return (
      <ModuleDisabledState
        projectId={projectId}
        title="Gestión del negocio"
        description="Este módulo incorpora ítems operativos, caja, inventario y contabilidad."
      />
    );
  }

  if (!visibleTabs.length) {
    return (
      <Alert severity="warning">
        Tu acceso está activo, pero no tenés permisos para consultar la gestión del negocio. Pedile al titular que revise tu rol.
      </Alert>
    );
  }

  return (
    <Stack spacing={{ xs: 1.6, md: 2.5 }} sx={{ width: "100%", minWidth: 0 }}>
      {error ? (
        <Alert severity="error" onClose={() => setError("")} sx={{ borderRadius: 3 }}>
          {error}
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          position: "relative",
          overflow: "hidden",
          borderRadius: { xs: 3, md: 4 },
          p: { xs: 2, sm: 2.6, md: 3.2 },
          color: "#fff",
          background: "linear-gradient(135deg, #19242F 0%, #243746 58%, #304B5C 100%)",
          boxShadow: "0 18px 45px rgba(25,36,47,.16)",
          "&::before": {
            content: '""',
            position: "absolute",
            width: { xs: 170, md: 260 },
            height: { xs: 170, md: 260 },
            borderRadius: "50%",
            bgcolor: "rgba(243,90,58,.22)",
            top: { xs: -105, md: -155 },
            right: { xs: -65, md: -70 },
          },
          "&::after": {
            content: '""',
            position: "absolute",
            width: 130,
            height: 130,
            borderRadius: "50%",
            border: "28px solid rgba(255,255,255,.035)",
            bottom: -84,
            left: "44%",
          },
        }}
      >
        <Stack
          direction={{ xs: "column", md: "row" }}
          justifyContent="space-between"
          alignItems={{ md: "flex-start" }}
          spacing={{ xs: 1.8, md: 3 }}
          sx={{ position: "relative", zIndex: 1 }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Typography
              variant="overline"
              sx={{
                display: "block",
                color: "rgba(255,255,255,.63)",
                fontWeight: 950,
                letterSpacing: 1.15,
                lineHeight: 1.2,
                mb: 0.75,
              }}
            >
              CENTRO OPERATIVO
            </Typography>
            <Typography
              variant="h3"
              sx={{
                color: "#fff",
                fontSize: { xs: "1.65rem", sm: "2rem", md: "2.45rem" },
                fontWeight: 950,
                letterSpacing: "-.035em",
                lineHeight: 1.05,
              }}
            >
              Gestión del negocio
            </Typography>
            <Typography
              sx={{
                color: "rgba(255,255,255,.72)",
                mt: 0.8,
                maxWidth: 680,
                fontSize: { xs: ".86rem", sm: ".96rem" },
                lineHeight: 1.45,
              }}
            >
              <Box component="span" sx={{ color: "#fff", fontWeight: 900 }}>
                {resolvedProject?.name || projectId}
              </Box>
              {" · "}registrá ventas, compras y gastos una sola vez y seguí caja, stock y resultado desde el mismo lugar.
            </Typography>

            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 1.6 }}>
              <Chip
                label={projectRoleLabel(access.role)}
                size="small"
                sx={{ bgcolor: "rgba(255,255,255,.13)", color: "#fff", fontWeight: 900 }}
              />
              {access.scopes.ownOperationsOnly ? (
                <Chip
                  label="Solo operaciones propias"
                  size="small"
                  variant="outlined"
                  sx={{ color: "#fff", borderColor: "rgba(255,255,255,.24)", fontWeight: 800 }}
                />
              ) : null}
              {access.blindCashClosing ? (
                <Chip
                  label="Arqueo sin saldo previo"
                  size="small"
                  variant="outlined"
                  sx={{ color: "#FFD7AE", borderColor: "rgba(255,183,112,.42)", fontWeight: 800 }}
                />
              ) : null}
            </Stack>
          </Box>

          {canBootstrap ? (
            <Button
              variant="outlined"
              startIcon={<RefreshRoundedIcon />}
              disabled={bootstrapping}
              onClick={refreshBootstrap}
              sx={{
                alignSelf: { xs: "stretch", sm: "flex-start" },
                flexShrink: 0,
                minHeight: 43,
                px: 2,
                borderRadius: 2.5,
                textTransform: "none",
                fontWeight: 900,
                color: "#fff",
                borderColor: "rgba(255,255,255,.28)",
                bgcolor: "rgba(255,255,255,.06)",
                "&:hover": {
                  borderColor: "rgba(255,255,255,.48)",
                  bgcolor: "rgba(255,255,255,.12)",
                },
                "&.Mui-disabled": { color: "rgba(255,255,255,.45)", borderColor: "rgba(255,255,255,.14)" },
              }}
            >
              {bootstrapping ? "Actualizando..." : "Actualizar datos"}
            </Button>
          ) : null}
        </Stack>

        <Box
          sx={{
            position: "relative",
            zIndex: 1,
            display: { xs: "none", md: "grid" },
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 1,
            mt: 2.6,
          }}
        >
          {WORKFLOW_STEPS.map((step) => (
            <Stack
              key={step.number}
              direction="row"
              spacing={1.1}
              alignItems="center"
              sx={{
                minWidth: 0,
                p: 1.25,
                borderRadius: 2.6,
                bgcolor: "rgba(255,255,255,.075)",
                border: "1px solid rgba(255,255,255,.08)",
              }}
            >
              <Box
                sx={{
                  width: 32,
                  height: 32,
                  flex: "0 0 auto",
                  borderRadius: 2,
                  display: "grid",
                  placeItems: "center",
                  bgcolor: ERP_BRAND.orange,
                  color: "#fff",
                  fontWeight: 950,
                }}
              >
                {step.number}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ color: "#fff", fontWeight: 900, fontSize: ".78rem", lineHeight: 1.15 }}>
                  {step.title}
                </Typography>
                <Typography noWrap sx={{ color: "rgba(255,255,255,.57)", fontSize: ".67rem", mt: 0.25 }}>
                  {step.detail}
                </Typography>
              </Box>
            </Stack>
          ))}
        </Box>
      </Paper>

      {needsMoneySetup ? (
        <Alert
          severity="info"
          action={(
            <Button
              color="inherit"
              size="small"
              onClick={() => navigate(`${base}/caja?crear=1`)}
              sx={{ fontWeight: 900, whiteSpace: "nowrap" }}
            >
              Crear la primera
            </Button>
          )}
          sx={{
            borderRadius: 3,
            alignItems: "center",
            "& .MuiAlert-message": { minWidth: 0 },
            "& .MuiAlert-action": { pl: { xs: 0.5, sm: 2 }, mr: 0 },
          }}
        >
          <strong>Primero indicá dónde está tu dinero.</strong> Creá Efectivo, una cuenta bancaria, Mercado Pago o cualquier caja real. Después registrá productos y operaciones.
        </Alert>
      ) : null}

      <Paper
        elevation={0}
        sx={{
          borderRadius: { xs: 3, md: 4 },
          border: `1px solid ${ERP_BRAND.border}`,
          bgcolor: "#fff",
          p: { xs: 1.8, sm: 2.3, md: 2.7 },
          boxShadow: "0 10px 30px rgba(25,36,47,.045)",
        }}
      >
        <Stack direction={{ xs: "column", lg: "row" }} spacing={{ xs: 2, lg: 3 }} alignItems={{ lg: "center" }} justifyContent="space-between">
          <Box sx={{ minWidth: 0, flex: "1 1 520px" }}>
            <Typography variant="overline" sx={{ color: ERP_BRAND.orange, fontWeight: 950, letterSpacing: 0.9 }}>
              EMPEZÁ POR ACÁ
            </Typography>
            <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950, fontSize: { xs: "1.08rem", sm: "1.28rem" }, mt: 0.15 }}>
              {starterCopy.title}
            </Typography>
            <Typography sx={{ color: ERP_BRAND.muted, fontSize: { xs: ".84rem", sm: ".91rem" }, lineHeight: 1.5, mt: 0.55, maxWidth: 760 }}>
              {starterCopy.detail}
            </Typography>
            <Stack direction="row" spacing={0.7} flexWrap="wrap" useFlexGap sx={{ mt: 1.45 }}>
              {BENEFIT_ITEMS.map(([label, detail]) => (
                <Chip
                  key={label}
                  size="small"
                  label={`${label} → ${detail}`}
                  sx={{ bgcolor: "#F7F9FB", color: "rgba(25,36,47,.78)", fontWeight: 800, border: "1px solid rgba(25,36,47,.07)" }}
                />
              ))}
            </Stack>
          </Box>
          {starterActions.length ? (
            <Stack direction={{ xs: "column", sm: "row", lg: "column" }} spacing={0.8} sx={{ minWidth: { lg: 220 }, flex: "0 0 auto" }}>
              {starterActions.map((action) => (
                <Button
                  key={action.key}
                  variant={action.primary ? "contained" : "outlined"}
                  startIcon={action.icon}
                  onClick={() => navigate(action.href)}
                  sx={{ justifyContent: "flex-start", minHeight: 42, borderRadius: 2.4, textTransform: "none", fontWeight: 900 }}
                >
                  {action.label}
                </Button>
              ))}
            </Stack>
          ) : null}
        </Stack>
      </Paper>

      <Paper
        elevation={0}
        sx={{
          minWidth: 0,
          borderRadius: { xs: 3, md: 4 },
          border: `1px solid ${ERP_BRAND.border}`,
          bgcolor: "rgba(255,255,255,.93)",
          boxShadow: "0 10px 30px rgba(25,36,47,.06)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ px: { xs: 1.35, sm: 2 }, pt: { xs: 1.35, sm: 1.8 }, pb: 0.8 }}>
          <Typography sx={{ color: ERP_BRAND.dark, fontWeight: 950, fontSize: { xs: ".92rem", sm: "1rem" } }}>
            Elegí dónde seguir
          </Typography>
          <Typography sx={{ display: { xs: "none", sm: "block" }, color: ERP_BRAND.muted, fontSize: ".78rem", mt: 0.25 }}>
            Lo operativo aparece primero. Las herramientas contables siguen disponibles, pero no necesitás dominarlas para empezar.
          </Typography>
        </Box>

        <Tabs
          value={currentTab}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          aria-label="Secciones de gestión del negocio"
          sx={{
            minHeight: { xs: 74, sm: 82, lg: 96 },
            px: { xs: 0.75, sm: 1.2 },
            pb: { xs: 0.9, sm: 1.2 },
            "& .MuiTabs-flexContainer": { gap: { xs: 0.7, sm: 0.85 } },
            "& .MuiTabs-indicator": { display: "none" },
            "& .MuiTabs-scrollButtons": {
              width: 34,
              borderRadius: 2,
              color: ERP_BRAND.dark,
              "&.Mui-disabled": { opacity: 0.2 },
            },
            "& .MuiTab-root": {
              minWidth: { xs: 112, sm: 136, md: 145, lg: 0 },
              maxWidth: { xs: 132, sm: 175, lg: "none" },
              flex: { lg: "1 1 0" },
              minHeight: { xs: 66, sm: 74, lg: 86 },
              alignItems: "flex-start",
              justifyContent: "center",
              textAlign: "left",
              gap: { xs: 0.65, sm: 0.9 },
              px: { xs: 1, sm: 1.25 },
              py: { xs: 0.9, sm: 1.1 },
              borderRadius: { xs: 2.3, sm: 2.7 },
              border: "1px solid rgba(25,36,47,.08)",
              bgcolor: "#F7F9FB",
              color: "rgba(25,36,47,.7)",
              textTransform: "none",
              transition: "transform .16s ease, border-color .16s ease, background-color .16s ease, box-shadow .16s ease",
              "& .MuiTab-iconWrapper": {
                m: "0 !important",
                flex: "0 0 auto",
                width: { xs: 31, sm: 36 },
                height: { xs: 31, sm: 36 },
                borderRadius: 2,
                display: "grid",
                placeItems: "center",
                bgcolor: "rgba(25,36,47,.06)",
                "& svg": { fontSize: { xs: 18, sm: 20 } },
              },
              "&:hover": {
                transform: { md: "translateY(-1px)" },
                borderColor: "rgba(243,90,58,.28)",
                bgcolor: "rgba(243,90,58,.035)",
              },
              "&.Mui-selected": {
                color: ERP_BRAND.dark,
                borderColor: "rgba(243,90,58,.42)",
                bgcolor: "rgba(243,90,58,.075)",
                boxShadow: "0 8px 18px rgba(243,90,58,.10)",
                "& .MuiTab-iconWrapper": { bgcolor: ERP_BRAND.orange, color: "#fff" },
              },
            },
          }}
        >
          {visibleTabs.map((tab) => (
            <Tab
              key={tab.key}
              value={tab.key}
              icon={tab.icon}
              iconPosition="start"
              label={<PrimaryTabLabel tab={tab} badge={tab.key === "accounting" && !accountingStarted ? "MÁS ADELANTE" : ""} />}
              onClick={() => navigate(tab.path ? `${base}/${tab.path}` : base)}
            />
          ))}
        </Tabs>
      </Paper>

      {currentTab === "accounting" && visibleAccountingTabs.length ? (
        <Paper
          elevation={0}
          sx={{
            borderRadius: 3,
            border: `1px solid ${ERP_BRAND.border}`,
            bgcolor: "rgba(255,255,255,.9)",
            p: { xs: 1, sm: 1.25 },
            overflow: "hidden",
          }}
        >
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            spacing={{ xs: 0.7, md: 1.4 }}
          >
            <Typography
              variant="caption"
              sx={{
                px: 0.7,
                flex: "0 0 auto",
                color: ERP_BRAND.muted,
                fontWeight: 950,
                letterSpacing: 0.65,
              }}
            >
              HERRAMIENTAS AVANZADAS
            </Typography>
            <Tabs
              value={currentAccountingTab}
              variant="scrollable"
              scrollButtons="auto"
              allowScrollButtonsMobile
              aria-label="Secciones contables"
              sx={{
                minWidth: 0,
                flex: 1,
                minHeight: 44,
                "& .MuiTabs-flexContainer": { gap: 0.6 },
                "& .MuiTabs-indicator": { display: "none" },
                "& .MuiTab-root": {
                  minHeight: 42,
                  minWidth: "auto",
                  px: 1.35,
                  borderRadius: 2.2,
                  textTransform: "none",
                  fontSize: ".78rem",
                  fontWeight: 850,
                  color: "rgba(25,36,47,.65)",
                  "& .MuiTab-iconWrapper": { mr: 0.65, "& svg": { fontSize: 18 } },
                  "&.Mui-selected": { color: "#fff", bgcolor: ERP_BRAND.dark },
                },
              }}
            >
              {visibleAccountingTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.path}
                  icon={tab.icon}
                  iconPosition="start"
                  label={tab.label}
                  onClick={() => navigate(`${base}/${tab.path}`)}
                />
              ))}
            </Tabs>
          </Stack>
        </Paper>
      ) : null}

      <Box sx={{ minWidth: 0 }}>
        <Outlet
          context={{
            project: resolvedProject,
            access,
            bootstrap,
            refreshBootstrap,
            setGlobalError: setError,
            can: (permission) => hasProjectPermission(access, permission),
          }}
        />
      </Box>
    </Stack>
  );
}
